import { useEffect } from 'react';

const SITE_NAME = 'Soif de Provence';
const BASE_URL = 'https://soifdeprovence.fr';

function upsertMeta(name, content, isProperty = false) {
  if (!content) return;
  const attr = isProperty ? 'property' : 'name';
  let meta = document.querySelector(`meta[${attr}="${name}"]`);
  if (!meta) {
    meta = document.createElement('meta');
    meta.setAttribute(attr, name);
    document.head.appendChild(meta);
  }
  meta.setAttribute('content', content);
}

function resolveUrl(url) {
  if (!url) return '';
  return url.startsWith('http') ? url : `${BASE_URL}${url}`;
}

function upsertCanonical(canonical) {
  if (!canonical) return;
  let link = document.querySelector('link[rel="canonical"]');
  if (!link) {
    link = document.createElement('link');
    link.setAttribute('rel', 'canonical');
    document.head.appendChild(link);
  }
  link.setAttribute('href', resolveUrl(canonical));
}

function applyOpenGraph({ title, description, canonical, og }) {
  upsertMeta('og:title', og.title || title, true);
  upsertMeta('og:description', og.description || description, true);
  upsertMeta('og:type', og.type || 'website', true);
  upsertMeta('og:site_name', SITE_NAME, true);
  upsertMeta('og:locale', 'fr_FR', true);
  upsertMeta('og:url', resolveUrl(canonical), true);
  upsertMeta('og:image', og.image, true);
}

function applyTwitter(title, description) {
  upsertMeta('twitter:card', 'summary_large_image');
  upsertMeta('twitter:title', title);
  upsertMeta('twitter:description', description);
}

function applyGeoTags() {
  upsertMeta('geo.region', 'FR-13');
  upsertMeta('geo.placename', 'Saint-Rémy-de-Provence');
  upsertMeta('geo.position', '43.7892;4.8312');
  upsertMeta('ICBM', '43.7892, 4.8312');
}

/**
 * Custom hook for managing SEO meta tags (title, description, canonical, OG, Twitter, geo).
 */
export default function useSEO({ title, description, keywords = '', canonical = '', og = {} }) {
  useEffect(() => {
    document.title = title ? `${title} | ${SITE_NAME}` : `${SITE_NAME} - Brasserie artisanale à Saint-Rémy-de-Provence`;
    upsertMeta('description', description);
    upsertMeta('keywords', keywords);
    upsertCanonical(canonical);
    applyOpenGraph({ title, description, canonical, og });
    applyTwitter(title, description);
    applyGeoTags();
  }, [title, description, keywords, canonical, og]);
}

// SEO configuration for all pages - centralized for easy management
export const SEO_CONFIG = {
  home: {
    title: 'Brasserie artisanale à Saint-Rémy-de-Provence',
    description: 'Soif de Provence : brasserie artisanale à Saint-Rémy-de-Provence. Bières artisanales des Alpilles, bière locale de Provence et location de tireuse à bière pour vos événements.',
    keywords: 'brasserie artisanale Saint-Rémy-de-Provence, bière artisanale Saint-Rémy-de-Provence, bière artisanale Alpilles, bière locale Provence, brasserie Provence, location de tireuse à bière',
    canonical: '/'
  },
  gamme: {
    title: 'Bières artisanales des Alpilles - Notre gamme',
    description: 'Découvrez nos bières artisanales brassées à Saint-Rémy-de-Provence : Blanche, Blonde, IPA, Porter, Ambrée, Golden Strong Ale, Stout. Bière locale de Provence, au cœur des Alpilles.',
    keywords: 'bière artisanale Alpilles, bière artisanale Saint-Rémy-de-Provence, bière locale Provence, IPA artisanale, Porter, Stout, brasserie Provence',
    canonical: '/nos-bieres'
  },
  professionnels: {
    title: 'Bière artisanale pour professionnels (CHR, cavistes)',
    description: 'Bière artisanale locale pour restaurants, bars, campings et cavistes en Provence. Fûts de 30 L, bière pression pour événement, livraison les mardis et jeudis. Brasserie des Alpilles médaillée.',
    keywords: 'bière artisanale professionnels, fût bière 30 litres, bière pression événement, brasserie Provence, fournisseur bière restaurant bar caviste, brasserie des Alpilles',
    canonical: '/professionnels'
  },
  pointsDeVente: {
    title: 'Où acheter nos bières - Points de vente en Provence',
    description: 'Où acheter la bière artisanale Soif de Provence ? Retrouvez nos revendeurs dans les Alpilles et les Bouches-du-Rhône (Saint-Rémy-de-Provence, Châteaurenard, Arles, Avignon, Orange) et en vente directe à la brasserie.',
    keywords: 'où acheter bière artisanale Provence, point de vente bière Alpilles, revendeur bière Saint-Rémy-de-Provence, bière locale Provence, brasserie Provence',
    canonical: '/points-de-vente'
  },
  contact: {
    title: 'Contact - Brasserie à Saint-Rémy-de-Provence',
    description: 'Contactez la brasserie artisanale Soif de Provence à Saint-Rémy-de-Provence : demande de devis, grille tarifaire professionnels, location de tireuse à bière et événements.',
    keywords: 'contact brasserie Saint-Rémy-de-Provence, devis bière artisanale, location de tireuse à bière, brasserie Provence',
    canonical: '/contact'
  },
  about: {
    title: 'À propos - Brasserie artisanale en Provence',
    description: 'Découvrez Soif de Provence, brasserie artisanale à Saint-Rémy-de-Provence. Notre passion pour la bière artisanale des Alpilles et la bière locale de Provence.',
    keywords: 'brasserie artisanale Saint-Rémy-de-Provence, histoire brasserie, bière artisanale Alpilles, brasserie Provence, artisan brasseur',
    canonical: '/a-propos'
  },
  tapRental: {
    title: 'Location de tireuse à bière - Événements en Provence',
    description: 'Location de tireuse à bière et fûts de bière artisanale pour vos événements à Saint-Rémy-de-Provence et dans les Alpilles : mariages, anniversaires, fêtes. Bière pression pour événement, livraison et installation.',
    keywords: 'location de tireuse à bière, bière pression événement, fût bière artisanale, louer tireuse Saint-Rémy-de-Provence, bière mariage anniversaire, brasserie Provence',
    canonical: '/location-tireuse'
  },
  legalMentions: {
    title: 'Mentions légales',
    description: 'Mentions légales du site Soif de Provence, brasserie artisanale à Saint-Rémy-de-Provence.',
    keywords: '',
    canonical: '/mentions-legales'
  },
  privacy: {
    title: 'Politique de confidentialité',
    description: 'Politique de confidentialité et protection des données personnelles de Soif de Provence.',
    keywords: '',
    canonical: '/confidentialite'
  }
};
